/**
 * AciOverridesTable Component
 *
 * Table for managing per-capacity-type ACI resource overrides.
 *
 * Feature: 017-backup-infrastructure-setup
 * Task: T024 - Create AciOverridesTable component
 * Constitution: v1.8.0 (Multi-Subscription SQL MI Edition)
 *
 * Requirements:
 * - FR-005: Per-capacity-type resource overrides
 */

import React, { useState } from 'react';
import {
  AciCapacityOverrideDto,
  CapacityType,
  JobPriority,
  RestartPolicy,
  CAPACITY_TYPES,
  JOB_PRIORITIES,
  RESTART_POLICIES,
} from '../types';
import {
  useCapacityOverrides,
  useUpsertCapacityOverride,
  useDeleteCapacityOverride,
} from '../hooks/useInfrastructure';
import {
  DualScrollTable,
  usePagination,
  PaginationControls,
} from '../../../components/common/TableControls';

interface AciOverridesTableProps {
  onUpdate?: () => void;
}

interface EditingOverride {
  capacityType: CapacityType;
  cpuCores: number | null;
  memoryGb: number | null;
  timeoutMinutes: number | null;
  restartPolicy: RestartPolicy | null;
  priority: JobPriority;
  maxConcurrentJobs: number;
  isActive: boolean;
}

const getCapacityTypeColor = (type: CapacityType): string => {
  switch (type) {
    case 'Platform':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
    case 'Reference':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
    case 'ClientDedicated':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    case 'MultiTenant':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
};

const getPriorityColor = (priority: JobPriority): string => {
  switch (priority) {
    case 'Critical':
      return 'text-red-600 dark:text-red-400';
    case 'High':
      return 'text-orange-600 dark:text-orange-400';
    default:
      return 'text-gray-600 dark:text-gray-400';
  }
};

export const AciOverridesTable: React.FC<AciOverridesTableProps> = ({ onUpdate }) => {
  const [editingType, setEditingType] = useState<CapacityType | null>(null);
  const [editForm, setEditForm] = useState<EditingOverride | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const { data: overridesData, isLoading, error, refetch } = useCapacityOverrides();
  const upsertMutation = useUpsertCapacityOverride();
  const deleteMutation = useDeleteCapacityOverride();

  const overrides = overridesData?.items || [];
  const existingTypes = new Set(overrides.map((o) => o.capacityType));
  const availableTypes = CAPACITY_TYPES.filter((t) => !existingTypes.has(t));

  const { page, setPage, pageSize, changeSize, total, totalPages, pageItems, start } =
    usePagination(overrides);

  const startEditing = (override: AciCapacityOverrideDto) => {
    setEditingType(override.capacityType);
    setEditForm({
      capacityType: override.capacityType,
      cpuCores: override.cpuCores,
      memoryGb: override.memoryGb,
      timeoutMinutes: override.timeoutMinutes,
      restartPolicy: override.restartPolicy,
      priority: override.priority,
      maxConcurrentJobs: override.maxConcurrentJobs,
      isActive: override.isActive,
    });
  };

  const startAdding = (capacityType: CapacityType) => {
    setShowAddModal(false);
    setEditingType(capacityType);
    setEditForm({
      capacityType,
      cpuCores: null,
      memoryGb: null,
      timeoutMinutes: null,
      restartPolicy: null,
      priority: 'Normal',
      maxConcurrentJobs: 5,
      isActive: true,
    });
  };

  const cancelEditing = () => {
    setEditingType(null);
    setEditForm(null);
  };

  const saveOverride = async () => {
    if (!editForm) return;

    try {
      await upsertMutation.mutateAsync({
        capacityType: editForm.capacityType,
        override: {
          cpuCores: editForm.cpuCores,
          memoryGb: editForm.memoryGb,
          timeoutMinutes: editForm.timeoutMinutes,
          restartPolicy: editForm.restartPolicy,
          priority: editForm.priority,
          maxConcurrentJobs: editForm.maxConcurrentJobs,
          isActive: editForm.isActive,
        },
      });
      cancelEditing();
      refetch();
      onUpdate?.();
    } catch (err) {
      console.error('Failed to save override:', err);
    }
  };

  const deleteOverride = async (capacityType: CapacityType) => {
    if (!confirm(`Delete override for ${capacityType}?`)) return;

    try {
      await deleteMutation.mutateAsync(capacityType);
      refetch();
      onUpdate?.();
    } catch (err) {
      console.error('Failed to delete override:', err);
    }
  };

  if (isLoading) {
    return (
      <div className='bg-[var(--color-bg-card)] rounded-lg shadow-sm border border-[var(--color-border)] p-6'>
        <div className='animate-pulse space-y-4'>
          <div className='h-4 bg-[var(--color-bg-subtle)] rounded w-1/3'></div>
          <div className='h-32 bg-[var(--color-bg-subtle)] rounded'></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4'>
        <p className='text-sm text-red-700 dark:text-red-400'>Failed to load capacity overrides: {error.message}</p>
      </div>
    );
  }

  return (
    <div className='bg-[var(--color-bg-card)] rounded-lg shadow-sm border border-[var(--color-border)]'>
      {/* Header */}
      <div className='px-6 py-4 border-b border-[var(--color-border)] flex items-center justify-between'>
        <div>
          <h3 className='text-lg font-medium text-[var(--color-text-primary)]'>Capacity Type Overrides</h3>
          <p className='mt-1 text-sm text-[var(--color-text-secondary)]'>
            Override default ACI settings for specific capacity types
          </p>
        </div>
        {availableTypes.length > 0 && (
          <div className='relative'>
            <button
              onClick={() => setShowAddModal(!showAddModal)}
              className='inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500'
            >
              <span className='mr-1'>+</span> Add Override
            </button>
            {showAddModal && (
              <div className='absolute right-0 mt-2 w-48 bg-[var(--color-bg-card)] rounded-md shadow-lg z-10 border border-[var(--color-border)]'>
                {availableTypes.map((type) => (
                  <button
                    key={type}
                    onClick={() => startAdding(type)}
                    className='block w-full text-left px-4 py-2 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]'
                  >
                    {type}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      <DualScrollTable>
        <table className='min-w-full divide-y divide-[var(--color-border)]'>
          <thead className='bg-[var(--color-bg-subtle)]'>
            <tr>
              <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                Capacity Type
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                CPU Cores
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                Memory (GB)
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                Timeout
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                Priority
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                Max Jobs
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                Status
              </th>
              <th className='px-6 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className='bg-[var(--color-bg-card)] divide-y divide-[var(--color-border)]'>
            {overrides.length === 0 ? (
              <tr>
                <td colSpan={8} className='px-6 py-8 text-center text-sm text-[var(--color-text-secondary)]'>
                  No capacity overrides configured. Default ACI settings will be used for all
                  capacity types.
                </td>
              </tr>
            ) : (
              pageItems.map((override) => (
                <tr key={override.capacityType} className='hover:bg-[var(--color-bg-subtle)]'>
                  {editingType === override.capacityType ? (
                    // Edit mode
                    <>
                      <td className='px-6 py-4'>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCapacityTypeColor(override.capacityType)}`}
                        >
                          {override.capacityType}
                        </span>
                      </td>
                      <td className='px-6 py-4'>
                        <input
                          type='number'
                          value={editForm?.cpuCores ?? ''}
                          onChange={(e) =>
                            setEditForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    cpuCores: e.target.value ? parseFloat(e.target.value) : null,
                                  }
                                : null
                            )
                          }
                          placeholder='default'
                          min={0.1}
                          max={4.0}
                          step={0.1}
                          className='w-20 px-2 py-1 text-sm border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded'
                        />
                      </td>
                      <td className='px-6 py-4'>
                        <input
                          type='number'
                          value={editForm?.memoryGb ?? ''}
                          onChange={(e) =>
                            setEditForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    memoryGb: e.target.value ? parseFloat(e.target.value) : null,
                                  }
                                : null
                            )
                          }
                          placeholder='default'
                          min={0.5}
                          max={16.0}
                          step={0.5}
                          className='w-20 px-2 py-1 text-sm border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded'
                        />
                      </td>
                      <td className='px-6 py-4'>
                        <input
                          type='number'
                          value={editForm?.timeoutMinutes ?? ''}
                          onChange={(e) =>
                            setEditForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    timeoutMinutes: e.target.value
                                      ? parseInt(e.target.value)
                                      : null,
                                  }
                                : null
                            )
                          }
                          placeholder='default'
                          min={1}
                          max={1440}
                          className='w-20 px-2 py-1 text-sm border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded'
                        />
                      </td>
                      <td className='px-6 py-4'>
                        <select
                          value={editForm?.priority || 'Normal'}
                          onChange={(e) =>
                            setEditForm((prev) =>
                              prev ? { ...prev, priority: e.target.value as JobPriority } : null
                            )
                          }
                          className='px-2 py-1 text-sm border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded'
                        >
                          {JOB_PRIORITIES.map((p) => (
                            <option key={p} value={p}>
                              {p}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className='px-6 py-4'>
                        <input
                          type='number'
                          value={editForm?.maxConcurrentJobs || 5}
                          onChange={(e) =>
                            setEditForm((prev) =>
                              prev ? { ...prev, maxConcurrentJobs: parseInt(e.target.value) } : null
                            )
                          }
                          min={1}
                          max={20}
                          className='w-16 px-2 py-1 text-sm border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded'
                        />
                      </td>
                      <td className='px-6 py-4'>
                        <label className='flex items-center'>
                          <input
                            type='checkbox'
                            checked={editForm?.isActive ?? true}
                            onChange={(e) =>
                              setEditForm((prev) =>
                                prev ? { ...prev, isActive: e.target.checked } : null
                              )
                            }
                            className='h-4 w-4 text-[var(--color-accent)] border-[var(--color-border)] rounded'
                          />
                          <span className='ml-2 text-sm text-[var(--color-text-secondary)]'>Active</span>
                        </label>
                      </td>
                      <td className='px-6 py-4 text-right'>
                        <button
                          onClick={saveOverride}
                          disabled={upsertMutation.isPending}
                          className='text-[var(--color-accent)] hover:opacity-80 text-sm font-medium mr-2'
                        >
                          Save
                        </button>
                        <button
                          onClick={cancelEditing}
                          className='text-[var(--color-text-secondary)] hover:opacity-80 text-sm font-medium'
                        >
                          Cancel
                        </button>
                      </td>
                    </>
                  ) : (
                    // View mode
                    <>
                      <td className='px-6 py-4'>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCapacityTypeColor(override.capacityType)}`}
                        >
                          {override.capacityType}
                        </span>
                      </td>
                      <td className='px-6 py-4 text-sm text-[var(--color-text-primary)]'>
                        {override.cpuCores !== null ? (
                          override.cpuCores
                        ) : (
                          <span className='text-[var(--color-text-secondary)]'>default</span>
                        )}
                      </td>
                      <td className='px-6 py-4 text-sm text-[var(--color-text-primary)]'>
                        {override.memoryGb !== null ? (
                          override.memoryGb
                        ) : (
                          <span className='text-[var(--color-text-secondary)]'>default</span>
                        )}
                      </td>
                      <td className='px-6 py-4 text-sm text-[var(--color-text-primary)]'>
                        {override.timeoutMinutes !== null ? (
                          `${override.timeoutMinutes} min`
                        ) : (
                          <span className='text-[var(--color-text-secondary)]'>default</span>
                        )}
                      </td>
                      <td className='px-6 py-4 text-sm'>
                        <span className={getPriorityColor(override.priority)}>
                          {override.priority}
                        </span>
                      </td>
                      <td className='px-6 py-4 text-sm text-[var(--color-text-primary)]'>
                        {override.maxConcurrentJobs}
                      </td>
                      <td className='px-6 py-4'>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            override.isActive
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {override.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className='px-6 py-4 text-right'>
                        <button
                          onClick={() => startEditing(override)}
                          className='text-[var(--color-accent)] hover:opacity-80 text-sm font-medium mr-3'
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteOverride(override.capacityType)}
                          disabled={deleteMutation.isPending}
                          className='text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm font-medium'
                        >
                          Delete
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DualScrollTable>
      <PaginationControls
        page={page}
        pageSize={pageSize}
        total={total}
        totalPages={totalPages}
        start={start}
        shown={pageItems.length}
        onPage={setPage}
        onPageSize={changeSize}
      />

      {/* Info */}
      <div className='px-6 py-4 bg-[var(--color-bg-subtle)] border-t border-[var(--color-border)]'>
        <p className='text-xs text-[var(--color-text-secondary)]'>
          Override settings take precedence over defaults. Use "default" values to inherit from
          default settings.
        </p>
      </div>
    </div>
  );
};

export default AciOverridesTable;
